import { CalLot, MeanBiasAndSlope } from "./cal-lot.model";
import { QcLot } from "./qc-lot.model";
import { AdjustedCalFormat, CalculationResponse, InstrumentFileInfo, Run, RunConfig, SampleSummaryResult } from "./result.model";
import { CalAdjustedDisplayOrder, FileMetaData, ReferenceFileInfoResponse, ReportContentItemResponse, RunSampleLot, Sample } from "./run.model";
import { Panel } from "./assays.model";
import { SampleInfo } from "./calculate.model";
import { SavedCoaDetails } from "./coa-details.model";
import { CalAdjustmentMode, isCalAdjustFinal, isCalibratorAdjustmentRunType } from "../utils/run-type.util";


/**
 * Represents a Study with various configurations, associated lots, and analysis results.
 * The `Study` class holds all details related to a specific study, including metadata, 
 * samples, and results of the analysis performed on them.
 */
export class Study {
  constructor(
    studyId: string,
  ) {
    this.studyId = studyId;
  }

  studyId!: string; /** Unique identifier for the study. */
  studyName!: string; /** Name of the study. */
  runType!: string; /** Type of the run phase for the study. */
  panelSpecs!: Panel | null; /** Specifications for the panel used in the study. Can be `null` if not applicable. */
  /**
   * Certificate of Analysis facts the C of A screen holds. `null` for a study saved before they
   * existed, or one whose C of A screen has never been opened.
   *
   * Typed as the saved shape rather than the current one because a study loaded from the API holds
   * whatever the client that saved it wrote. `normalizeCoaDetails` migrates it on read.
   */
  coaDetails?: SavedCoaDetails | null;
  testSpec!: string; /** The test specification phase for the study */
  assignSpec!: string; /** Assignment specification type for the study. */
  refKitLot!: string; /** Reference kit lot identifier used in the study. */
  patientFileMetadata!: FileMetaData[]; /** Metadata for the patient files associated with the study. */
  calFileMetadata!: FileMetaData[]; /** Metadata for the calibration files used in the study. */
  controlFileMetadata!: FileMetaData[]; /** Metadata for the control files used in the study. */
  runSamples!: Sample[]; /** Samples that were part of the study run. */
  runSampleLots!: RunSampleLot[];
  reportContentItems!: ReportContentItemResponse; /** Report content items generated for the study's analysis. */
  selectedCalcReportContentItemId?: string | null; /** The selected report content item ID for the calculation report. This is optional and may be `null`. */
  selectedSampleReportContentItemId?: string | null; /** The selected report content item ID for the sample report. This is optional and may be `null`. */
  calcFailedSamplesMode?: boolean; /** Calculations > Sample shows failed, alerted and discarded rows across every report content item. */
  sampleFailedSamplesMode?: boolean; /** Sample Analysis > List shows failed and alerted rows across every report content item. */
  selectedControlAnalysisReportContentItemId?: string | null;  /** The selected report content item ID for the control analysis report. This is optional and may be `null`. */
  selectedControlCurveReportContentItemId?: string | null; /** The selected report content item ID for the control curve report. This is optional and may be `null`. */
  selectedAssignmentReportContentItemId?: string | null; /** The selected report content item ID for the assignment report. This is optional and may be `null`. */
  // runValidityStatus!: ResultStatusEnum; /** Status of the run's validity. */
  //runValidity!: RunValidity[]; /** List of the run validity information for the study. */
  calLots!: CalLot[]; /** Calibration lots associated with the study. */
  adjustedCalLots!: AdjustedCalFormat; /** Adjusted calibration lots for the study. */
  calAdjustedDisplayOrder?: CalAdjustedDisplayOrder; /** The order to display adjusted calibration lots according to the user-defined Run Report Items table. */
  meanBiasAndSlope!: MeanBiasAndSlope; /** Mean bias and slope values for the study's analysis. */
  qcLots!: QcLot[]; /** control lots associated with the study. */
  kitLots!: string[]; /** Kit lot identifiers used in the study. */
  rmqcAssayCount?: number; /** Number of assay rows each selected kit lot holds in the Run Report Items table. */
  assayNames!: string[]; /** List of assay names used in the study. */
  beadRegions!: number[]; /** List of bead regions for the study. */
  //regressionStats!: RegressionStats[]; /** Regression statistics related to the study. */
  calculationResponse!: CalculationResponse | null; /** Calculation response for the study, can be `null` if no calculation was performed. */
  calculationAdjustedResponse!: CalculationResponse | null; /** Adjusted calculation response for the study, can be `null` if no adjusted calculation was performed. */
  sampleDataOriginal?: SampleSummaryResult[]; /** Cached samples from pull_sample_data (original calculation). */
  sampleDataAdjusted?: SampleSummaryResult[]; /** Cached samples from pull_sample_data (adjusted calculation). */
  sampleDataFailuresOriginal?: SampleSummaryResult[]; /** Failure-only subset for original calcs. */
  sampleDataFailuresAdjusted?: SampleSummaryResult[]; /** Failure-only subset for adjusted calcs. */
  //runStatus!: RunStatus | null; /** Run status for the study, can be `null` if the run status is not determined. */
  activeAssayTab?: string | null; /** The currently active assay tab for the study. This is optional and may be `null`. */
  assaySummaryColumnSelection?: string[]; /** Persisted column selections for the Assay Summary tab. */
  assayDetailsColumnSelection?: string[]; /** Persisted column selections for the Assay Details tab. */
  comments?: string; /** Comments or notes related to the study. */
  calculate_averages?: boolean; /** Indicates whether mean calculations should be included in the calculation request. */
  use_legacy_4pl?: boolean; /** Indicates whether to use the legacy 4PL calculation path. */
  optimize_adjustment?: boolean; /** Forces run value optimization for calculation requests (UI-only). */
  skip_missing_samples?: boolean; /** Indicates whether missing patient samples should be skipped in the calculation request. */
  /**
   * Operator's override of the Allowed adjustment factor (%) on Adjusted Calibrator.
   * `null` means they never set one, so the screen seeds from the calibration administration
   * default for the study's panel and workflow.
   */
  allowed_adjustment_factor?: number | null;
  /** Calibrator Adjustment only: whether the results are the Adjust dual run or the single Final run. */
  cal_adjustment_mode?: CalAdjustmentMode | null;
  runDataPanels?: ReferenceFileInfoResponse | null; /** Run data panels associated with the study, can be `null` if not applicable. */
  auditTrail?: AuditTrailEntry[]; /** Audit trail entries for the study. */
  isCommitted?: boolean; /** Indicates whether the study has been committed. */
  isDraft?: boolean; /** Indicates whether the study was loaded from a draft. */
  customStudyId?: string | null; /** Human-readable sequential study identifier (BPX-YY-XXXX format) from the backend. Null for draft studies. */
  unadjustedCalculationId?: string | null; /** Last known unadjusted calculation ID for persisted studies. */
  adjustedCalculationId?: string | null; /** Last known adjusted calculation ID for persisted studies. */
}

export interface AuditTrailEntry {
  name: string;
  userId?: string | null;
  isSuperUser?: boolean | null;
  date: string | null;
  action?: string | null;
  appVersion?: string | null;
  comment?: string | null;
}

/**
 * Represents the data associated with a study, including the study itself and any related comments.
 */
export class StudyData {
    /**
   * Constructs a new StudyData instance.
   * 
   * @param study The study object containing all study-related data.
   * @param comments Additional comments or notes for the study.
   */
  constructor(
    public study: Study,
    public comments: string
  ) {        
    this.study = study; /**  The study instance. */
    this.comments = comments; /** Additional comments or notes related to the study. */
  }
}



/**
 * Represents the request to retrieve a list of studies based on the specified criteria.
 */
export class StudyListRequest {
  panel_name!: string;
  workflow!: string;
  user_name?: string;
}

/**
 * Represents the response to a study list request.
 */
export class StudyListResponse {
  studies!: StudyListItem[]; /** List of study items. */
}

export class StudyListItem {
  run_name!: string; /** Name of the study. */
  is_draft!: boolean; /** Indicates whether the study is a draft. */
  study_id?: string | null;
  is_committed?: boolean;
  user?: string;
  date?: string | null;
}

export type DraftStudyRun = {
  name: string;
  status?: string;
  state?: string;
  spec_kit_name?: string;
  workflow?: string;
  test_spec?: string;
  formulation_stage?: string;
  notes?: string;
  update?: boolean;
  calculate_averages?: boolean;
  use_legacy_4pl?: boolean;
  skip_missing_samples?: boolean;
  allowed_adjustment_factor?: number | null;
  cal_adjustment_mode?: CalAdjustmentMode | null;
};

export type DraftStudySampleInfo = {
  accession_number?: string[];
  sample_id?: (string | null)[];
  sample_type?: string[];
  num_replicates?: number[];
};

export type LotVersionPayload = {
  lotNumber: string;
  version?: number;
};

/**
 * Collapses repeated lot entries: one per lot number and version, first occurrence kept.
 *
 * An unversioned entry is also dropped when the same lot is listed with a version, since it is the
 * same lot from before the version was recorded. Two distinct versions of a lot are both kept.
 */
export function dedupeLotVersions<T extends { lotNumber: string; version?: number | null }>(entries: T[]): T[] {
  const versioned = new Set(entries.filter(entry => entry.version != null).map(entry => entry.lotNumber));
  const seen = new Set<string>();

  return entries.filter(entry => {
    if (entry.version == null && versioned.has(entry.lotNumber)) {
      return false;
    }
    const key = `${entry.lotNumber}::${entry.version ?? ''}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export type DraftLotEntry = string | {
  lotNumber: string;
  version?: number | null;
};

export type DraftStudyData = {
  run: DraftStudyRun;
  revisionNum?: string;
  cal_lot_nums?: DraftLotEntry[];
  ctrl_lot_nums?: DraftLotEntry[];
  report_content_item_ids?: string[];
  pack_lot_numbers?: Record<string, string>;
  run_config?: Partial<RunConfig>;
  sample_info?: DraftStudySampleInfo;
  instr_file_info?: Partial<InstrumentFileInfo>;
  notes?: string;
  unadjusted_calculation_id?: string;
  adjusted_calculation_id?: string;
  /** Certificate facts, for a draft saved from a study whose C of A screen had been opened. */
  coa_details?: SavedCoaDetails | null;
};

export type LoadDraftStudyResponse = {
  draft_data: DraftStudyData;
  created_date: string;
  last_modified_date: string;
  audit_trail: AuditTrailEntry[];
};

/**
 * Represents a request to save study data.
 */
export class SaveStudyRequest {
  /**
   * Constructs a new SaveStudyRequest instance.
   * 
   * @param study The study object to be saved.
   * @param panelName The panel name for the study.
   * @param panelPhase The phase of the panel for the study.
   * @param testSpecPhase The test specification phase for the study.
   * @param studyName The name of the study.
   * @param studyStatus The status of the study.
   * @param calculationStatus The status of the calculation for the study.
   */
  constructor(
    study: Study,
    panelName: string,
    panelPhase: string,
    testSpecPhase: string,
    studyName: string,
    studyStatus: boolean,
    calculationStatus: string,
    unadjustedCalculationId?: string,
    adjustedCalculationId?: string,
    sourceRunName?: string,
    relatedStudyNames?: string[]
  ) {
    // Deduped so a lot listed twice in state is not saved twice and restored as two sub tabs.
    this.cal_lot_nums = dedupeLotVersions(study.calLots.map(item =>
      this.toLotVersionPayload(item.calLotNumber, item.valueAssignmentVersion)
    ));
    this.ctrl_lot_nums = dedupeLotVersions(study.qcLots.map(item =>
      this.toLotVersionPayload(item.masterLotNumber, item.valueAssignmentVersion)
    ));
    const uniqueReportContentItems = Array.from(
      new Map(
        (study.reportContentItems?.reportContentItems ?? [])
          .filter(item => !!item?.reportContentItemId)
          .map(item => [item.reportContentItemId, item])
      ).values()
    );

    this.report_content_item_ids = uniqueReportContentItems
      .sort((a, b) => a.reportContentItemId.localeCompare(b.reportContentItemId))
      .map(item => item.reportContentItemId);
    const hasAdjustedData = hasAdjustedCalculationData(study);
    const result = transformReportContentItems(study, {
      includeValueAssignments: hasAdjustedData
    });
    this.pack_lot_numbers = result.pack_lot_numbers;
    this.run_config = result.run_config;
    const allFiles = study.patientFileMetadata.concat(study.calFileMetadata, study.controlFileMetadata);
    this.instr_file_info = {
      file_names: allFiles.map(item => item.fileName),
      file_types: allFiles.map(item => item.type),
      instrument_ids: allFiles.map(item => item.instrumentId),
      serial_numbers: allFiles.map(item => item.instrumentSerialNo),
      software_versions: allFiles.map(item => item.softwareVersion),
    };
    const runMetadata = this.createRunMetadata(study, panelName, panelPhase, testSpecPhase, studyName, calculationStatus, studyStatus);
    this.run = runMetadata;
    this.sample_info = new SampleInfo(study);
    this.revisionNum = study.panelSpecs?.revisionNumber || ""; 
    if (unadjustedCalculationId) {
      this.unadjusted_calculation_id = unadjustedCalculationId;
    }
    if (adjustedCalculationId) {
      this.adjusted_calculation_id = adjustedCalculationId;
    }
    if (sourceRunName) {
      this.source_run_name = sourceRunName;
    }
    // The list is authoritative on save: it replaces every linkage this study has. Send it
    // whenever the caller provides one — including [] to clear links — and omit it when undefined
    // so a save that does not manage links leaves existing links untouched.
    if (relatedStudyNames !== undefined) {
      this.related_study_names = relatedStudyNames;
    }
    // Only sent once the C of A screen has produced them. Omitting the key leaves whatever is
    // stored alone, so a save from a study whose certificate was never opened cannot wipe it.
    if (study.coaDetails) {
      this.coa_details = study.coaDetails;
    }
  }

  run!: Run;
  /** Certificate facts; omitted entirely when the study has none. */
  coa_details?: SavedCoaDetails;
  cal_lot_nums!: LotVersionPayload[];
  ctrl_lot_nums!: LotVersionPayload[];
  report_content_item_ids!: string[];
  pack_lot_numbers!: Record<string, string>;
  run_config!: RunConfig;
  instr_file_info!: InstrumentFileInfo;
  sample_info!: SampleInfo;
  revisionNum!: string; 
  unadjusted_calculation_id?: string;
  adjusted_calculation_id?: string;
  /**
   * On a Save-As (a saved study saved under a new name) where the unadjusted half was not rerun —
   * an adjusted calc id only, or no calc ids at all — names the study it came from so the backend
   * copies that study's calc responses + sample data forward to the new name.
   */
  source_run_name?: string;
  /** run_names of studies to link (MST-504). Authoritative on save; omit to leave links untouched. */
  related_study_names?: string[];
  //bulkMaterialLots!:

  private createRunMetadata(
    study: Study,
    panelName: string,
    panelPhase: string,
    testSpecPhase: string,
    studyName: string,
    calculationStatus: string,
    studyStatus: boolean
  ): Run {
    return {
      name: studyName,
      status: calculationStatus.toUpperCase() === "NA" ? "FAIL" : "PASS", //request only accepts Pass or Fail for now
      state: "Initial",
      spec_kit_name: panelName,
      stage: panelPhase,
      test_stage: testSpecPhase,
      workflow: this.normalizeWorkflow(study.runType),
      test_spec: this.normalizeTestSpec(study.testSpec),
      formulation_stage: study.assignSpec ?? null,
      notes: study.comments ?? "",
      update: studyStatus,
      calculate_averages: !!study.calculate_averages,
      use_legacy_4pl: !!study.use_legacy_4pl,
      skip_missing_samples: !!study.skip_missing_samples,
      // null (not 0) when unset, so the backend keeps falling back to the admin default.
      allowed_adjustment_factor: study.allowed_adjustment_factor ?? null,
      // Cal-adjust studies with no Final run are Adjust, which is also how the backend reads null.
      cal_adjustment_mode: isCalibratorAdjustmentRunType(study.runType)
        ? (study.cal_adjustment_mode ?? 'Adjust')
        : null
    };
  }

  private normalizeWorkflow(runType?: string): string {
    return (runType ?? "").replace(/\s+/g, "");
  }

  private normalizeTestSpec(testSpec?: string): string {
    if (!testSpec) {
      return "";
    }

    if (testSpec.trim().toLowerCase() === "adjustment") {
      return "CalibratorAdjustment";
    }

    return testSpec;
  }

  private toLotVersionPayload(lotNumber: string | undefined, version: number | undefined): LotVersionPayload {
    const normalizedLotNumber = String(lotNumber ?? "").trim();
    const normalizedVersion = typeof version === "number" && Number.isFinite(version)
      ? version
      : undefined;
    return normalizedVersion === undefined
      ? { lotNumber: normalizedLotNumber }
      : { lotNumber: normalizedLotNumber, version: normalizedVersion };
  }
}

/**
 * Represents a request to save a load a study.
 */
export class StudyLoadRequest {

  /**
  * Constructs a new SaveStudyRequest instance.
  * 
  * @param studyName The study object to be saved.
  */
  constructor(studyName: string) {
    this.run_name = studyName;
  }

  run_name!: string; /** Name of the study to be loaded. */
}

/**
 * Represents a request to save a load a study.
 */
export class StudyLoadResponse {

  /**
  * Constructs a new SaveStudyRequest instance.
  * 
  * @param studyName The study object to be saved.
  */
  constructor(study: Study) {
    this.study = study;
  }

  study!: Study; /** Name of the study to be loaded. */
}


/**
 * Represents a request to commit a study.
 */
export class CommitRequest {

  /**
  * Constructs a new CommitRequest instance.
  *
  * @param studyName The study object to be committed
  * @param notes Additional notes or comments related to the commit action.
  * @param reauthToken ID token from the Active Directory re-authentication performed before committing.
  */
  constructor(studyName: string, notes: string | undefined, reauthToken: string) {
    this.run_name = studyName;
    this.notes  = notes;
    this.reauth_token = reauthToken;
  }

  run_name!: string; /** Name of the study to be committed */
  notes: string | undefined; /** Additional notes or comments related to the commit action. */
  reauth_token!: string; /** ID token proving the operator re-authenticated; verified server-side. */
}





//function transformBulkMaterialItems(materialName: string,  items: ReportContentItem[]) {

//}


/**
 * Transforms the report content items into a structured format suitable for saving.
 * 
 * @param study The study containing the report content items and related metadata.
 * @returns An object containing the transformed report content item data.
 */
function transformReportContentItems(study: Study, options?: { includeValueAssignments?: boolean }) {
  const items = study.reportContentItems?.reportContentItems ?? [];
  const refLot = study.refKitLot;
  const includeValueAssignments = !!options?.includeValueAssignments;
  const isFinal = isCalAdjustFinal(study);
  const valueAssignmentsList = includeValueAssignments ? (study.adjustedCalLots?.valueAssignment ?? []) : [];
  const originalValueAssignmentsList = includeValueAssignments ? (study.adjustedCalLots?.originalValueAssignment ?? []) : [];
  const sampleOverrides = buildSampleTypeOverrideLookup(study);

  const pack_lot_numbers = new Map<string, string>();
  const assays = new Map<string, string>();
  const instrument_ids = new Map<string, string>();
  const cal_lots = new Map<string, string>();
  //const alt_assay_names = new Map<string, string>();
  const bead_regions = new Map<string, number>();
  const result_type = new Map<string, string>();
  const run_value_optimization = new Map<string, string>();
  const manual_filtering = new Map<string, string>();
  const sample_type_overrides = new Map<string, string>();
  const bead_coupling_date = new Map<string, string>();
  const coupled_bead_ln = new Map<string, string>();
  const dyed_bead_ln = new Map<string, string>();
  const progenitor_ln = new Map<string, string>();
  const antigen_ln = new Map<string, string>();
  const antigen_loading = new Map<string, string>();
  const ligand_pn = new Map<string, string>();
  const bead_region_pn = new Map<string, string>();
  const coupled_bead_pn = new Map<string, string>();
  const bead_reagent_ln = new Map<string, string>();
  const conjugate_reagent_ln = new Map<string, string>();
  const particle_diluent_ln = new Map<string, string>();
  const conjugate_diluent_ln = new Map<string, string>();
  const sheath_fluid_ln = new Map<string, string>();
  const sample_diluent_ln = new Map<string, string>();
  const wash_buffer_ln = new Map<string, string>();
  const description = new Map<string, string>();
  const reference_lots = new Map<string, string>();
  const value_assignments = new Map<string, string>();
  const original_value_assignments = new Map<string, string>();

  items.forEach((item, index) => {
    const key = item.reportContentItemId;
    if (!key) return;

    pack_lot_numbers.set(key, item.lot);
    assays.set(key, item.assay);
    instrument_ids.set(key, String(item.runId ?? ""));
    const calLotFromItem = item.calLot || study.calLots?.[0]?.calLotNumber || "";
    cal_lots.set(key, calLotFromItem);
    //alt_assay_names.set(key, item.assay?.toLowerCase() || "");
    bead_regions.set(key, item.region);
    const resultType = item.resultType;
    result_type.set(key, resultType || "");

    const assignmentValues = includeValueAssignments ? valueAssignmentsList[index] : undefined;
    const formattedAssignment = formatValueAssignment(assignmentValues);
    value_assignments.set(key, formattedAssignment);
    const originalAssignmentValues = includeValueAssignments ? originalValueAssignmentsList[index] : undefined;
    original_value_assignments.set(key, formatValueAssignment(originalAssignmentValues));
    const itemRunValueOptimization = (item as unknown as { runValueOptimization?: string | boolean }).runValueOptimization;
    // A Final run never optimizes, whatever the operator last picked for the Adjust run.
    const optimizationValue = isFinal ? 'N/A' : normalizeOptimizationOption(itemRunValueOptimization);
    run_value_optimization.set(key, optimizationValue);

    const manualFilteringSource = (item as unknown as { manualFiltering?: Record<string, unknown> | string })?.manualFiltering;
    const manualFilteringString = typeof manualFilteringSource === "string"
      ? manualFilteringSource
      : JSON.stringify(manualFilteringSource ?? {});
    manual_filtering.set(key, manualFilteringString);

    const overrides = sampleOverrides.get(key) ?? [];
    sample_type_overrides.set(key, JSON.stringify(overrides));

    bead_coupling_date.set(key, item.beadCouplingDate || "");
    coupled_bead_ln.set(key, item.coupledBeadLn || "");
    dyed_bead_ln.set(key, item.dyedBeadLn || "");
    progenitor_ln.set(key, item.progenitorLn || "");
    antigen_ln.set(key, item.antigenLn || "");
    antigen_loading.set(key, item.antigenLoadLn || "");
    ligand_pn.set(key, item.ligandPn || "");
    bead_region_pn.set(key, item.beadReagentPn|| ""); 
    coupled_bead_pn.set(key, item.coupledBeadPn || "");
    bead_reagent_ln.set(key, item.beadReagentLn || "");
    conjugate_reagent_ln.set(key, item.conjugateReagentLn || "");
    particle_diluent_ln.set(key, item.particleDiluentLn || "");
    conjugate_diluent_ln.set(key, item.conjugateDiluentLn || "");
    sheath_fluid_ln.set(key, item.sheathFluidLn || "");
    sample_diluent_ln.set(key, item.sampleDiluentLn || "");
    wash_buffer_ln.set(key, item.washBufferLn || "");
    description.set(key, item.description || "");
    reference_lots.set(key, refLot || "");
  });

  const mapToObjectString = (map: Map<string, string>) => Object.fromEntries(map.entries());
  const mapToObjectNumber = (map: Map<string, number>) => Object.fromEntries(map.entries());
  const mapToObjectOptimization = (map: Map<string, string>) => Object.fromEntries(map.entries());

  return {
    pack_lot_numbers: mapToObjectString(pack_lot_numbers),
    run_config: {
      assays: mapToObjectString(assays),
      instrument_ids: mapToObjectString(instrument_ids),
      cal_lots: mapToObjectString(cal_lots),
      bead_regions: mapToObjectNumber(bead_regions),
      reference_lots: mapToObjectString(reference_lots),
      value_assignments: mapToObjectString(value_assignments),
      original_value_assignments: mapToObjectString(original_value_assignments),
      run_value_optimization: mapToObjectOptimization(run_value_optimization),
      result_type: mapToObjectString(result_type),
      manual_filtering: mapToObjectString(manual_filtering),
      sample_type_overrides: mapToObjectString(sample_type_overrides),
      bead_coupling_date: mapToObjectString(bead_coupling_date),
      coupled_bead_ln: mapToObjectString(coupled_bead_ln),
      dyed_bead_ln: mapToObjectString(dyed_bead_ln),
      progenitor_ln: mapToObjectString(progenitor_ln),
      antigen_ln: mapToObjectString(antigen_ln),
      antigen_loading: mapToObjectString(antigen_loading),
      ligand_pn: mapToObjectString(ligand_pn),
      bead_region_pn: mapToObjectString(bead_region_pn),
      coupled_bead_pn: mapToObjectString(coupled_bead_pn),
      bead_reagent_ln: mapToObjectString(bead_reagent_ln),
      conjugate_reagent_ln: mapToObjectString(conjugate_reagent_ln),
      particle_diluent_ln: mapToObjectString(particle_diluent_ln),
      conjugate_diluent_ln: mapToObjectString(conjugate_diluent_ln),
      sheath_fluid_ln: mapToObjectString(sheath_fluid_ln),
      sample_diluent_ln: mapToObjectString(sample_diluent_ln),
      wash_buffer_ln: mapToObjectString(wash_buffer_ln),
      description: mapToObjectString(description),
    },
  };
}

function normalizeOptimizationOption(value: string | boolean | undefined): string {
  if (typeof value !== 'string') {
    return 'N/A';
  }

  const normalized = value.trim().toLowerCase().replace(/\s+/g, '_');
  if (normalized === 'n/a' || normalized === 'na') {
    return 'N/A';
  }
  if (normalized === 'powell') {
    return 'powell';
  }
  if (normalized === 'proportional_sweep') {
    return 'proportional_sweep';
  }

  return 'N/A';
}

function hasAdjustedCalculationData(study: Study): boolean {
  const adjustedResponse = study.calculationAdjustedResponse?.calculationResponse;
  if (!adjustedResponse || typeof adjustedResponse !== 'object') {
    return false;
  }

  // A Final run reports its finalized values as originalAssignment and leaves optimizedAssignment null.
  const isFinal = isCalAdjustFinal(study);
  return (adjustedResponse.runStatus ?? []).some(rs =>
    rs.calCurveInformation?.calSamples?.some(s =>
      s.optimizedAssignment != null || (isFinal && s.originalAssignment != null)
    )
  );
}

function buildSampleTypeOverrideLookup(study: Study): Map<string, string[]> {
  const overrides = new Map<string, string[]>();
  const defaultSampleTypes = study.runSamples?.map(sample => sample.sampleType || sample.defaultSampleType || "") ?? [];
  const runSampleLots = study.runSampleLots ?? [];

  runSampleLots.forEach(runSampleLot => {
    const lotId = String(runSampleLot?.reportContentItemId ?? "");
    if (!lotId) {
      return;
    }

    const runSamples = runSampleLot.runSamples ?? [];
    if (!runSamples.length) {
      overrides.set(lotId, []);
      return;
    }

    const overridesList = runSamples.map(sample => sample.sampleType ?? "");
    const hasDifferences = overridesList.some((sampleType, index) => sampleType !== (defaultSampleTypes[index] ?? ""));
    overrides.set(lotId, hasDifferences ? overridesList : []);
  });

  return overrides;
}

function formatValueAssignment(values?: number[]): string {
  if (!values || !values.length) {
    return "[]";
  }

  return `[${values.join(",")}]`;
}
